// Where a task came from, as the row and the detail need to say it. Import-free on purpose (see
// orion-web/CLAUDE.md → Tests): the card, the fold header and the detail panel all read it, and a jest
// test must be able to exercise it without dragging the app's dependency graph in.
//
// The bug this exists for: two tasks a goal had laid out sat on a real Tasks screen reading "In progress"
// with nothing to say WHY either row existed. A row that names its origin answers that in one glance —
// which is the whole point, so the name is a LINK to the thing it names, not a decorative label.

/** The fields a row needs to name its goal. Satisfied by `WebTask` and by the task-detail payload. */
export interface GoalOriginTask {
  /** The goal whose plan laid this task out. */
  goalId?: string;
  /** That goal's title, joined by the route — absent when the goal could not be read. */
  goalTitle?: string;
  /** The Space the task lives in; its SLUG is what makes the goal route resolvable. */
  workspaceId?: string;
}

export interface GoalOrigin {
  goalId: string;
  /** The goal's own title, shown verbatim. Never a fallback like "Untitled goal": a row that cannot name
   *  its origin renders no origin line at all, which is honest, where a placeholder is just noise. */
  title: string;
  /**
   * Where the name points, or undefined when this browser cannot build the route.
   *
   * A goal is only addressable through its WORKSPACE (`useWorkspaceGoals(workspaceId)` here,
   * `fetchGoals(workspaceId)` on native — no surface can resolve a goal from its id alone), so an
   * unresolved slug means there is no honest destination. The line then still NAMES the goal and simply
   * does not link, rather than rendering a link that lands nowhere.
   */
  href?: string;
}

/**
 * The web route for one goal: its workspace's Goals tab, with that goal selected. Same shape
 * `lib/app-link.ts` resolves `workspace/<slug>/goal/<id>` to — one route for a goal, so a provenance
 * link and a deeplink land on the same screen.
 */
export function goalRoutePath(workspaceSlug: string, goalId: string): string {
  return `/workspace/${encodeURIComponent(workspaceSlug)}/goals?goal=${encodeURIComponent(goalId)}`;
}

/**
 * The goal a task came from, ready to render — or null when there is nothing honest to show (no goal on
 * the task, or a goal whose title we could not read).
 *
 * `workspaceSlug` is looked up by the caller from the workspaces roster it already holds; passing none
 * yields a named-but-unlinked origin rather than nothing, for the reason on `href`.
 */
/**
 * The CHAT a task was born in — the origin every task has, and the one the drill-in never showed.
 *
 * A goal is only SOME tasks' origin, and a routine only some others'; the conversation is on the entity
 * for all of them (`sourceConversationId` is required and non-null — the v1→v2 mapper refuses to run
 * without one). So "where did this come from" has an answer for every task, and this is it.
 *
 * It takes the ALREADY-GATED id rather than the raw source conversation, deliberately. A task raised by
 * a routine carries its PULSE thread and one created by a goal tool carries that tool's conversation;
 * neither is in the user's chat list, so linking to them would land nowhere. `/api/task/[id]` already
 * makes exactly that distinction for the resolve CTA (`maestroSourceConversationId` — a SCHEDULER source
 * only), and this reuses its verdict instead of asking the same question a second way. No openable
 * conversation means no line, which is the same rule `taskGoalOrigin` follows for an unreadable goal.
 */
export function taskChatOrigin(task: { resolveConversationId?: string }): { conversationId: string } | null {
  const conversationId = task.resolveConversationId?.trim();
  return conversationId ? { conversationId } : null;
}

export function taskGoalOrigin(task: GoalOriginTask, workspaceSlug?: string): GoalOrigin | null {
  const goalId = task.goalId?.trim();
  const title = task.goalTitle?.trim();
  if (!goalId || !title) return null;
  const slug = workspaceSlug?.trim();
  return { goalId, title, ...(slug ? { href: goalRoutePath(slug, goalId) } : {}) };
}
